import Component from '@glimmer/component';

// ---------------------------------------------------------------------------
// Chip – reimagined, zero-Frontile chip / badge / tag component
// ---------------------------------------------------------------------------

/**
 * Visual intent/severity options
 */
export type ChipIntent = 'primary' | 'danger' | 'warning' | 'success' | 'default';

/**
 * Size options
 */
export type ChipSize = 'sm' | 'md' | 'lg';

/**
 * Visual appearance / variant
 */
export type ChipAppearance = 'default' | 'outlined' | 'faded';

export interface ChipSignature {
  Args: {
    /** Visual intent */
    intent?: ChipIntent;
    /** Size */
    size?: ChipSize;
    /** Visual appearance (default = filled, outlined = bordered, faded = subtle bg) */
    appearance?: ChipAppearance;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLSpanElement;
}

/**
 * A lightweight status badge / tag used in cards, tables, and list items.
 *
 * Built on the `.app-chip*` semantic class family — intent classes set
 * the `--chip-bg` and `--chip-fg` CSS custom properties from the
 * canonical `--tag-*` token recipes, then appearance modifiers compose
 * those properties (filled / outlined-with-ring / faded-with-color-mix).
 * That keeps the CSS to ~5 intents + 3 appearances rather than 5 × 3
 * intent-appearance pairs.
 *
 * ```gts
 * <Chip @intent="success" @size="sm">Active</Chip>
 * <Chip @intent="primary" @appearance="faded">Enrolled</Chip>
 * <Chip @intent="danger" @appearance="outlined">Rejected</Chip>
 * ```
 *
 * @public
 */
export default class Chip extends Component<ChipSignature> {
  get intent() {
    return this.args.intent ?? 'default';
  }

  get size() {
    return this.args.size ?? 'md';
  }

  get appearance() {
    return this.args.appearance ?? 'default';
  }

  get appearanceClass() {
    // The "default" appearance is the bare .app-chip baseline (filled
    // with intent bg + fg) so it doesn't carry an explicit modifier.
    return this.appearance === 'default' ? '' : `app-chip--${this.appearance}`;
  }

  <template>
    <span
      data-test-chip
      data-test-chip-intent={{this.intent}}
      data-test-chip-size={{this.size}}
      class="app-chip app-chip--{{this.intent}} app-chip--{{this.size}} {{this.appearanceClass}}"
      ...attributes
    >
      {{yield}}
    </span>
  </template>
}
